import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useGetUserProfileQuery } from "@api/accountManagement";
import { BettoSettingsSidebar } from "../../components/BettoSettingsSidebar";
import { BettoContentColumn, BettoProfilePageRoot, BettoSettingsGrid } from "../../shared/elements";
import { DEFAULT_SETTINGS_TAB, SETTINGS_TABS } from "../../constants";
import { AccountInfoSection } from "./sections/AccountInfoSection";
import SecuritySection from "./sections/SecuritySection";
import PreferencesSection from "./sections/PreferencesSection";
import { PersonalVerificationSection } from "./sections/PersonalVerificationSection";
import { PaymentMethodsSection } from "./sections/PaymentMethodsSection";

const getTabFromHash = (hash) => {
	const tabId = hash?.replace("#", "");
	return SETTINGS_TABS.some((t) => t.id === tabId) ? tabId : DEFAULT_SETTINGS_TAB;
};

export const BettoProfilePage = memo(
	({ translate, user, toggleLoading, showAlert, initialTab, history, location }) => {
		const { data: userInfo } = useGetUserProfileQuery();
		const [activeTab, setActiveTab] = useState(() =>
			initialTab || getTabFromHash(location?.hash)
		);

		useEffect(() => {
			if (initialTab) {
				setActiveTab(initialTab);
				return;
			}
			setActiveTab(getTabFromHash(location?.hash));
		}, [location?.hash, initialTab]);

		const handleTabChange = useCallback(
			(tabId) => {
				setActiveTab(tabId);
				if (!history || !location) return;
				history.replace({ pathname: location.pathname, hash: tabId });
			},
			[history, location]
		);

		const showSecurityWarning = useMemo(
			() => !user?.mfa_enabled || !(userInfo?.email || user?.email),
			[user, userInfo]
		);

		const sectionContent = useMemo(() => {
			switch (activeTab) {
				case "security":
					return <SecuritySection translate={translate} toggleLoading={toggleLoading} />;
				case "preferences":
					return <PreferencesSection translate={translate} showAlert={showAlert} />;
				case "verification":
					return <PersonalVerificationSection translate={translate} />;
				case "payment":
					return <PaymentMethodsSection translate={translate} history={history} />;
				default:
					return <AccountInfoSection translate={translate} />;
			}
		}, [activeTab, translate, toggleLoading, showAlert, history]);

		return (
			<BettoProfilePageRoot>
				<BettoSettingsGrid>
					<BettoSettingsSidebar
						activeTab={activeTab}
						onTabChange={handleTabChange}
						translate={translate}
						showSecurityWarning={showSecurityWarning}
					/>
					<BettoContentColumn>{sectionContent}</BettoContentColumn>
				</BettoSettingsGrid>
			</BettoProfilePageRoot>
		);
	}
);

BettoProfilePage.displayName = "BettoProfilePage";

BettoProfilePage.propTypes = {
	translate: PropTypes.func.isRequired,
	user: PropTypes.object,
	toggleLoading: PropTypes.func,
	showAlert: PropTypes.func,
	initialTab: PropTypes.string,
	history: PropTypes.object,
	location: PropTypes.object,
};
